// frontend/src/components/InstallBanner.jsx
// Smart "Add to Home Screen" banner that detects platform and shows instructions.
// Remembers dismissal via localStorage for 30 days.

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../utils/i18n';
import { X, Download, Share, Plus, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'wafiyo_install_dismissed';
const DISMISS_DAYS = 14;

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function getDevice() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

export default function InstallBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const device = getDevice();

  // Listen for Chrome's native install prompt (Android)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Show banner only on mobile, not already installed, not dismissed recently
  useEffect(() => {
    if (isStandalone()) return; // Already installed
    if (device === 'desktop') return; // Only show on mobile

    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch (e) {
      console.warn('localStorage access denied', e);
    }

    // Small delay so the banner doesn't flash on page load
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [device]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch (e) {
      console.warn('localStorage access denied', e);
    }
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        dismiss();
      }
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, dismiss]);

  if (!visible) return null;

  return (
    <div className="install-banner" style={{
      position: 'fixed',
      top: 16,
      left: 16,
      right: 16,
      zIndex: 9999,
      background: 'linear-gradient(135deg, #1A7A4A 0%, #0d5c35 100%)',
      borderRadius: 16,
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      animation: 'slideDown 0.4s ease-out',
      color: '#fff',
      maxWidth: 420,
      margin: '0 auto'
    }}>
      {/* Icon */}
      <div style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: 'rgba(255,255,255,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Smartphone size={22} color="#fff" />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
          {t('install_banner_title')}
        </div>
        <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.4 }}>
          {device === 'ios' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>{t('install_banner_ios')}</div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                background: 'rgba(0,0,0,0.15)', 
                padding: '6px 12px', 
                borderRadius: 8,
                width: 'fit-content'
              }}>
                {/* iOS Share Icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                  <polyline points="16 6 12 2 8 6"></polyline>
                  <line x1="12" y1="2" x2="12" y2="15"></line>
                </svg>
                <span style={{ fontSize: 14, opacity: 0.7 }}>→</span>
                {/* Add to Home Screen Icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="4" ry="4"></rect>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
              </div>
            </div>
          ) : (
            t('install_banner_android')
          )}
        </div>
      </div>

      {/* Action */}
      {device === 'android' && deferredPrompt ? (
        <button
          onClick={handleInstallClick}
          style={{
            background: '#fff',
            color: '#1A7A4A',
            border: 'none',
            borderRadius: 10,
            padding: '8px 16px',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0
          }}
        >
          <Download size={14} />
          {t('install_banner_btn')}
        </button>
      ) : null}

      {/* Close */}
      <button
        onClick={dismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          padding: 4,
          flexShrink: 0,
          display: 'flex'
        }}
        aria-label="Dismiss"
      >
        <X size={18} />
      </button>

      {/* Slide-up animation */}
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
